// Formation view: the chosen form is built on the blackboard one rule step at a time (पढ → पढि → पढि + हिमि → पढिहिमि).
// Plays by itself, can be paused and stepped; with reduced motion every step is shown at once and nothing moves.

import type { FC, ReactNode } from 'react'
import type { Formation, Part } from '@/prakrit/lib/grammar/formation'

import { useEffect, useState } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    Pause,
    Play,
    RotateCcw
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-media-query'
import { Bi } from '@/prakrit/components/common/bi'
import { SECONDARY_BUTTON, hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'

const STEP_MS = 1600

const chalkClass = (part: Part): string => {
    if (part.role === 'ending') return 'rounded-md bg-chalk/10 px-1 text-chalk-accent'
    if (part.role === 'changed') return 'text-chalk-accent'
    if (part.role === 'plus') return 'font-sans font-normal text-chalk-dim'

    return 'text-chalk'
}

const BoardParts: FC<{ parts: Part[], className?: string }> = ({ parts, className }): ReactNode => (
    <span lang='pra' className={cn('chalk font-semibold', className)}>
        {parts.map((part, i) => <span key={i} className={chalkClass(part)}>{part.text}</span>)}
    </span>
)

interface FormationPlayerProps {
    formation: Formation
    /** What is being built, e.g. "भविष्यत् काल · उत्तम पुरुष एकवचन". */
    caption: ReactNode
}

export const FormationPlayer: FC<FormationPlayerProps> = ({ formation, caption }): ReactNode => {
    const reduced = useReducedMotion()
    const total = formation.steps.length
    const signature = formation.steps.map(s => s.parts.map(p => p.text).join('')).join('>')
    const [step, setStep] = useState(0)
    const [playing, setPlaying] = useState(false)

    // A new form starts again from its root; with reduced motion all steps are shown at once.
    useEffect(() => {
        setStep(reduced ? total - 1 : 0)
        setPlaying(!reduced)
    }, [signature, reduced, total])

    useEffect(() => {
        if (!playing) return

        if (step >= total - 1) {
            setPlaying(false)

            return
        }

        const timer = window.setTimeout(() => setStep(s => Math.min(total - 1, s + 1)), STEP_MS)

        return () => window.clearTimeout(timer)
    }, [playing, step, total])

    const current = formation.steps[Math.min(step, total - 1)]
    const atEnd = step >= total - 1

    const go = (next: number) => {
        setPlaying(false)
        setStep(Math.max(0, Math.min(total - 1, next)))
    }

    const replay = () => {
        setStep(0)
        setPlaying(!reduced)
    }

    return (
        <div className='flex flex-col gap-4'>
            <div className='board-surface flex min-h-[15rem] flex-col justify-between gap-5 rounded-2xl px-5 py-5 sm:px-8 sm:py-7'>
                <div className='flex flex-wrap items-center justify-between gap-2 text-sm text-chalk-dim'>
                    <span>{caption}</span>
                    <span aria-hidden='true' className='hindi'>चरण {hiDigits(step + 1)} / {hiDigits(total)}</span>
                </div>

                <div aria-live='polite' className='flex flex-col items-center gap-3 text-center'>
                    <p key={`${signature}-${step}`} className='animate-chalk-in'>
                        <BoardParts parts={current.parts} className='text-[clamp(2rem,1.4rem+3vw,3.25rem)] leading-[1.35]' />
                    </p>
                    <p key={`${signature}-${step}-note`} className='animate-chalk-in max-w-xl text-[0.9375rem] text-chalk-dim'>
                        <span lang='hi' data-ex='hi' className='hindi'>{current.note.hi}</span>
                        <span aria-hidden='true' className='ex-sep'> · </span>
                        <span lang='en' data-ex='en'>{current.note.en}</span>
                    </p>
                    <span className='sr-only'>चरण {step + 1} / {total} · step {step + 1} of {total}</span>
                </div>

                <div aria-hidden='true' className='flex justify-center gap-1.5'>
                    {formation.steps.map((_, i) => (
                        <span key={i} className={cn('h-1.5 rounded-full transition-all', i <= step ? 'w-6 bg-chalk-accent' : 'w-3 bg-chalk/25')} />
                    ))}
                </div>
            </div>

            <div className='flex flex-wrap items-center gap-2'>
                <button type='button' onClick={() => go(step - 1)} disabled={step === 0} className={SECONDARY_BUTTON}>
                    <ChevronLeft className='size-4' aria-hidden='true' />
                    <Bi hi='पिछला' en='Back' layout='inline' enClassName='text-xs text-muted-foreground' always />
                </button>
                {playing ? (
                    <button type='button' onClick={() => setPlaying(false)} className={SECONDARY_BUTTON}>
                        <Pause className='size-4' aria-hidden='true' />
                        <Bi hi='रोकें' en='Pause' layout='inline' enClassName='text-xs text-muted-foreground' always />
                    </button>
                ) : (
                    <button type='button' onClick={atEnd ? replay : () => setPlaying(true)} className={SECONDARY_BUTTON}>
                        {atEnd ? <RotateCcw className='size-4' aria-hidden='true' /> : <Play className='size-4' aria-hidden='true' />}
                        <Bi hi={atEnd ? 'फिर से' : 'चलाएँ'} en={atEnd ? 'Replay' : 'Play'} layout='inline' enClassName='text-xs text-muted-foreground' always />
                    </button>
                )}
                <button type='button' onClick={() => go(step + 1)} disabled={atEnd} className={SECONDARY_BUTTON}>
                    <Bi hi='अगला' en='Next' layout='inline' enClassName='text-xs text-muted-foreground' always />
                    <ChevronRight className='size-4' aria-hidden='true' />
                </button>
            </div>

            <ol className='flex flex-col gap-1.5'>
                {formation.steps.map((s, i) => (
                    <li key={i}>
                        <button
                            type='button'
                            onClick={() => go(i)}
                            aria-current={i === step ? 'step' : undefined}
                            className={cn(
                                'flex min-h-tap w-full items-center gap-3 rounded-xl border px-3 py-1.5 text-left transition-colors',
                                i === step ? 'border-prakrit/35 bg-prakrit/6' : 'border-border bg-card hover:border-saffron/60',
                                i > step && 'opacity-55'
                            )}
                        >
                            <span aria-hidden='true' className='flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-muted-foreground'>
                                {hiDigits(i + 1)}
                            </span>
                            <span className='flex min-w-0 flex-col'>
                                <span lang='pra' className='font-prakrit text-lg font-semibold'>
                                    {s.parts.map((part, j) => (
                                        <span
                                            key={j}
                                            className={part.role === 'ending' ? 'text-terracotta' : part.role === 'changed' ? 'text-saffron-strong' : part.role === 'plus' ? 'font-sans font-normal text-muted-foreground' : 'text-prakrit'}
                                        >
                                            {part.text}
                                        </span>
                                    ))}
                                </span>
                                <Bi hi={s.note.hi} en={s.note.en} layout='inline' className='text-xs text-muted-foreground' />
                            </span>
                        </button>
                    </li>
                ))}
            </ol>

            {formation.rule && (
                <p className='text-xs text-muted-foreground'>
                    <span lang='hi' className='hindi'>नियम: {formation.rule} · प्राकृत स्वयं-शिक्षक</span>
                </p>
            )}
        </div>
    )
}
